import React from 'react';
import { useTranslation } from 'react-i18next';
import { useTrackingPage, useSegment } from '../../components/tracking/trackingProvider';
import { WeezHeader, Button, AnchorNav, ToastContext } from '@weezevent/nacre';
import PropTypes from 'prop-types';
import Description from './description';
import LegalRepresentative from './legalRepresentative';
import GeneralInformation from './generalInformation';
import SwornStatement from './swornStatement';
import { useForm, requiredField, websiteField } from '../../utils/validators';
import Organization from '../../models/organization';
import MessageInformation from './messageInformation';
import useAppContext from '../../context/useAppContext';
import OrganizationInfos from '../../models/organizationInfos';
import Loading from '../../components/loading';
import FormErrorsSummary from '../../components/formErrorsSummary';

export default function OrganizerProfile({ organization, bank }) {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);
    const { track } = useSegment();
    const [context, setContext] = useAppContext();
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [errorsSummary, setErrorsSummary] = React.useState({});

    const validateAddress = React.useCallback((name, address, form) => {
        let subErrors = {};
        const fieldAdrress = ['address1', 'city', 'country', 'zip_code'];

        fieldAdrress.forEach(field => {
            if (!address[field] || !address[field].trim()) {
                subErrors[field] = t('ticket.common.required');
            }
        });

        if ((address['country'] === 'CA' || address['country'] === 'US') && (!address['state'] || !address['state'].trim())) {
            subErrors['state'] = t('ticket.common.required');
        }

        if (Object.keys(subErrors).length > 0) {
            return subErrors;
        }
        return true;
    }, []);

    const [organizationInfos, loadingOrganizationInfos] = OrganizationInfos.useApiModel({
        organizationId: organization.id
    });

    const validateSworn = React.useCallback((name, sworn, form) => {
        if (!sworn) {
            return t('ticket.common.required');
        }
        return true;
    }, []);

    const validatePhone = React.useCallback((name, phone, form) => {
        if (!phone) {
            return t('ticket.common.required');
        }
        const regexPhone = /^([(]?[+][0-9]{1,4}[)]?)?([ -]?[ (-]?[0-9][)]?){5,14}$/gm;
        if (!regexPhone.test(phone) || phone.length < 6) {
            return t('ticket.organiser.invalid_phone_number');
        }
        return true;
    }, []);

    const validators = React.useMemo(() => {
        return {
            name: requiredField,
            organization_type: requiredField,
            intra_community_vat: organization.business_entity.uuid === Organization.BUSINESS_ENTITY_SPAIN ? requiredField : false,
            last_name: requiredField,
            first_name: requiredField,
            phone_number: validatePhone,
            address: validateAddress,
            sworn: validateSworn,
            website: websiteField
        };
    }, [validatePhone, validateAddress, validateSworn]);

    const [formOrganisation, setFormOrganisation, , organisationErrors, organisationValidate] = useForm(
        {
            name: organization.name || '',
            organization_type: organization.organization_type || '',
            license_number: organization.license_number || '',
            intra_community_vat: organization.intra_community_vat || '',
            last_name: organization.last_name || '',
            first_name: organization.first_name || '',
            position: organization.position || '',
            address: {
                name: organization.address.zip_code || '',
                address1: organization.address.address1 || '',
                address2: organization.address.address2 || '',
                city: organization.address.city || '',
                country: organization.address.country || '',
                state: organization.address.state || '',
                zip_code: organization.address.zip_code || ''
            },
            phone_number: organization.phone_number || '',
            website: organization.website || '',
            sworn: false
        },
        validators
    );

    const fieldsNamesTradsErrors = React.useMemo(() => {
        return {
            name: t('ticket.organiser.description.formLabelName'),
            organization_type: t('ticket.organiser.description.legal_status'),
            intra_community_vat: t('ticket.organiser.description.eu_vat_number'),
            last_name: t('ticket.organiser.legal_representative.last_name'),
            first_name: t('ticket.organiser.legal_representative.first_name'),
            phone_number: t('ticket.organiser.general_information.phone'),
            address: {
                address1: t('ticket.organiser.general_information.address'),
                state: t('ticket.event.state'),
                zip_code: t('ticket.organiser.general_information.zip_code'),
                city: t('ticket.organiser.general_information.city'),
                country: t('ticket.organiser.general_information.country')
            },
            sworn: t(`ticket.organiser.sworn_statement.title`),
            website: t('ticket.organiser.general_information.website')
        };
    }, []);

    const onChangeForm = React.useCallback(
        (fieldName, value) => {
            const defaultOptions = {
                disableValidation: true,
                cleanErrors: true
            };

            setFormOrganisation(fieldName, value, defaultOptions);
        },
        [setFormOrganisation]
    );

    const handleSubmit = React.useCallback(() => {
        if (!organisationValidate(true, setErrorsSummary)) {
            return;
        }
        setIsSubmitting(true);

        const { name, organization_type, license_number, intra_community_vat, last_name, first_name, position, address, phone_number, website } = formOrganisation;
        const organisation = new Organization({
            id: organization.id,
            name,
            organization_type,
            license_number,
            intra_community_vat,
            last_name,
            first_name,
            position,
            address,
            phone_number,
            website
        });

        organisation
            .save({
                organizationId: organization.id
            })
            .then(({ response }) => {
                const organization = new Organization(response);
                setContext({ ...context, organization });
                toast.success(t(`ticket.organiser.saveSuccess`));

                let peopleIdentify = {
                    "Organization's Name": response.name,
                    "Organization's Legal Status": response.organization_type,
                    "Representative's Last Name": response.last_name,
                    "Representative's First Name": response.first_name,
                    "Representative's Function": response.position || '',
                    'Organization Full Address': response.address.address1,
                    'Organization Postcode': response.address.zip_code,
                    'Organization City': response.address.city,
                    'Organization Country': response.address.country,
                    Cellphone: response.phone_number,
                    Website: response.website || '',
                    'Organization Info Last Update': new Date().toUTCString()
                };
                let eventInfo = {
                    'Licence Number': response.license_number || '',
                    'EU VAT Number': response.intra_community_vat || '',
                    'Page Title': 'ORGANIZER PROFILE'
                };
                track('ACTION > SAVE PROFILE', eventInfo, peopleIdentify);
            })
            .catch(error => {
                toast.error(t(`ticket.organiser.saveError`));
            })
            .finally(() => {
                setIsSubmitting(false);
                context.syncOrganization();
            });
    }, [formOrganisation, organisationValidate, context]);

    useTrackingPage('VIEW - ORGANIZER PROFILE', {
        'Page Title': 'ORGANIZER PROFILE'
    });

    if (loadingOrganizationInfos) {
        return <Loading />;
    }

    return (
        <>
            <WeezHeader
                title={t(`ticket.organiser.pageTitle`)}
                rightComponent={[<Button primary key="primary" label={t('ticket.common.action.save')} onClick={handleSubmit} isLoading={isSubmitting} />]}
            >
                {t(`ticket.organiser.descriptionTitle`)}
            </WeezHeader>
            {!bank.iban && !bank.rbc_account && <MessageInformation organization={organization} />}
            <FormErrorsSummary formErrors={errorsSummary} fieldsTrads={fieldsNamesTradsErrors} />
            <AnchorNav
                rootId="main"
                items={[
                    {
                        id: 'organiser-form-description',
                        title: t('ticket.organiser.description.title')
                    },
                    {
                        id: 'organiser-form-legal_representative',
                        title: t('ticket.organiser.legal_representative.title')
                    },
                    {
                        id: 'organiser-form-general_information',
                        title: t('ticket.organiser.general_information.title')
                    }
                ]}
            >
                <Description
                    organization={organization}
                    formOrganisation={formOrganisation}
                    onChange={onChangeForm}
                    organisationErrors={organisationErrors}
                    organizationInfos={organizationInfos}
                />
                <LegalRepresentative formOrganisation={formOrganisation} onChange={onChangeForm} organisationErrors={organisationErrors} />
                <GeneralInformation formOrganisation={formOrganisation} onChange={onChangeForm} organization={organization} organisationErrors={organisationErrors} />
                <SwornStatement formOrganisation={formOrganisation} onChange={onChangeForm} organisationErrors={organisationErrors} />
            </AnchorNav>
        </>
    );
}

OrganizerProfile.propTypes = {
    organization: PropTypes.object.isRequired,
    bank: PropTypes.object.isRequired
};
